import { FaPlus, FaTimes } from 'react-icons/fa';
import { VideoEditSpecialtySectionProps } from './types';
import useVideoEditSpecialtySection from './useVideoEditSpecialtySection';
import styles from './VideoEditSpecialtySection.module.css';

const KEY_LIST_ID = 'video-edit-specialty-section__key-options';

/**
 * 診療科と、動画に付けるキー付きタグ（部位:膝 など）の編集セクション。
 * 診療科ごとの専用入力欄は持たず、どの科でも同じ「キー：値」の行で分類する。
 */
const VideoEditSpecialtySection = (props: VideoEditSpecialtySectionProps) => {
  const { specialties, selectedSpecialtyId, handleSpecialtyChange, attributes } = props;
  const { chosenSpecialty, keyOptions, getValueOptions, addAttribute, updateAttribute, removeAttribute } =
    useVideoEditSpecialtySection(props);

  return (
    <div className={styles['video-edit-specialty-section']}>
      <div className={styles['video-edit-specialty-section__form-group']}>
        <label className={styles['video-edit-specialty-section__label']} htmlFor="video-edit-specialty-section__specialty-select">
          診療科
        </label>
        <select
          id="video-edit-specialty-section__specialty-select"
          value={selectedSpecialtyId || ''}
          onChange={handleSpecialtyChange}
          className={styles['video-edit-specialty-section__select']}
          data-testid="video-edit-specialty-section__specialty-select"
        >
          <option value="">-- 未設定 --</option>
          {(specialties ?? []).map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <p className={styles['video-edit-specialty-section__hint']}>
          非公開のままなら未設定でも構いません。他の人に公開するときに必要になります。
        </p>
      </div>

      <div className={styles['video-edit-specialty-section__form-group']}>
        <div className={styles['video-edit-specialty-section__attributes-header']}>
          <span className={styles['video-edit-specialty-section__label']}>分類タグ</span>
          {chosenSpecialty && (
            <span className={styles['video-edit-specialty-section__specialty-subtitle']}>{chosenSpecialty.name}</span>
          )}
        </div>

        {attributes.length === 0 && (
          <p className={styles['video-edit-specialty-section__empty-message']} data-testid="video-edit-specialty-section__empty">
            分類タグはまだありません。「部位」「処置」など、探すときの手がかりになる項目を追加できます。
          </p>
        )}

        <datalist id={KEY_LIST_ID}>
          {keyOptions.map(key => (
            <option key={key} value={key} />
          ))}
        </datalist>

        <ul className={styles['video-edit-specialty-section__attribute-list']}>
          {attributes.map((attribute, index) => {
            const valueOptions = getValueOptions(attribute.attribute_key);
            const valueListId = `video-edit-specialty-section__value-options-${index}`;
            return (
              <li
                key={index}
                className={styles['video-edit-specialty-section__attribute-row']}
                data-testid={`video-edit-specialty-section__attribute-row-${index}`}
              >
                <input
                  type="text"
                  list={KEY_LIST_ID}
                  maxLength={64}
                  value={attribute.attribute_key}
                  onChange={e => updateAttribute(index, { attribute_key: e.target.value })}
                  className={`${styles['video-edit-specialty-section__input']} ${styles['video-edit-specialty-section__input--key']}`}
                  placeholder="項目（例: 部位）"
                  aria-label={`分類タグ ${index + 1} の項目`}
                  data-testid={`video-edit-specialty-section__attribute-key-${index}`}
                />
                <span className={styles['video-edit-specialty-section__separator']}>：</span>
                <input
                  type="text"
                  list={valueOptions.length > 0 ? valueListId : undefined}
                  maxLength={255}
                  value={attribute.attribute_value}
                  onChange={e => updateAttribute(index, { attribute_value: e.target.value })}
                  className={`${styles['video-edit-specialty-section__input']} ${styles['video-edit-specialty-section__input--value']}`}
                  placeholder="値（例: 膝）"
                  aria-label={`分類タグ ${index + 1} の値`}
                  data-testid={`video-edit-specialty-section__attribute-value-${index}`}
                />
                {valueOptions.length > 0 && (
                  <datalist id={valueListId}>
                    {valueOptions.map(option => (
                      <option key={option} value={option} />
                    ))}
                  </datalist>
                )}
                <button
                  type="button"
                  onClick={() => removeAttribute(index)}
                  className={styles['video-edit-specialty-section__remove-button']}
                  aria-label={`分類タグ ${index + 1} を削除`}
                  data-testid={`video-edit-specialty-section__attribute-remove-${index}`}
                >
                  <FaTimes />
                </button>
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          onClick={addAttribute}
          className={styles['video-edit-specialty-section__add-button']}
          data-testid="video-edit-specialty-section__add-attribute"
        >
          <FaPlus />
          分類タグを追加
        </button>
      </div>
    </div>
  );
};

export default VideoEditSpecialtySection;
